import {
	type PaymentAttempt,
	decidePaymentRecovery,
} from "@offstreetparking/portal-flow/components/CreatePermitWizard/components/PaymentForm";
import type { PaymentFormError } from "@offstreetparking/portal-flow/components/CreatePermitWizard/components/PaymentForm";
import { offstreetClient } from "~/services/offstreetApi";
import type { RouterOutputs } from "~/services/offstreetApi/types";
import { useElavon } from "../../hooks/useElavon";
type ElavonLightboxFormProps = {
	paymentSession: NonNullable<RouterOutputs["paymentSession"]["createPaymentSessionPublic"]["session"]>;
	formId: string;
	onError: (error: PaymentFormError) => void;
	onSuccess: (publicIdentifier: string) => void;
	setIsProcessing: (isProcessing: boolean) => void;
};
export const ElavonLightboxForm = ({
	paymentSession,
	formId,
	onSuccess,
	onError,
	setIsProcessing,
}: ElavonLightboxFormProps) => {
	const { isReady, openLightbox } = useElavon({
		paymentSession,
	});
	const { mutate: acknowledgeFailedPayment } = offstreetClient.paymentSession.acknowledgeFailedPayment.useMutation();
	const recover = (attempt: PaymentAttempt, publicIdentifier?: string) => {
		const { outcome, shouldAcknowledge } = decidePaymentRecovery(attempt);
		if (outcome === "succeeded") {
			onSuccess(publicIdentifier ?? "");
		} else {
			if (shouldAcknowledge) {
				acknowledgeFailedPayment({ paymentId: paymentSession.paymentId });
			}
			onError(outcome);
		}
		setIsProcessing(false);
	};
	const { mutate: completePaymentSession } = offstreetClient.paymentSession.completePaymentSession.useMutation({
		onSuccess: (data) =>
			recover(
				{ type: "completed", status: data.status },
				data.status === "succeeded" ? data.publicIdentifier : undefined,
			),
		onError: () => recover({ type: "request-failed" }),
	});
	const handleOpenLightbox = () => {
		if (!isReady) {
			return;
		}
		setIsProcessing(true);
		openLightbox({
			onApproval: (response) => {
				completePaymentSession({
					paymentSessionId: paymentSession.paymentId,
					token: response.ssl_txn_id,
				});
			},
			// Verify declined transaction IDs server-side before allowing another attempt.
			onDeclined: (response) => {
				completePaymentSession({
					paymentSessionId: paymentSession.paymentId,
					token: response.ssl_txn_id,
				});
			},
			onError: (error) => {
				if (error.errorCode === "NOT_LOADED") {
					setIsProcessing(false);
					return;
				}
				recover({ type: "lightbox-error" });
			},
			onCancelled: () => recover({ type: "lightbox-error" }),
		});
	};
	return (
		<form
			id={formId}
			onSubmit={(e) => {
				e.preventDefault();
				handleOpenLightbox();
			}}
		/>
	);
};
